import $ from 'jquery'
import Backbone from 'backbone'
import template from './multi-tree-selector.hbs'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import i18n from 'scoreboard/components/i18n'
import rowTemplate from './multi-tree-selector-row.hbs'
import _ from 'lodash'
import Breadcrumbs from 'scoreboard/components/breadcrumbs'
import CascadeTooltip from 'scoreboard/components/cascade-tooltip'
import spotlight from 'scoreboard/components/spotlight'
import FormView from 'scoreboard/components/form-view'
import TreeActions from 'scoreboard/components/tree-actions'
import LoadingDots from 'scoreboard/components/loading-dots'
import TreeStream from 'scoreboard/components/tree-stream'
import TwoColumnDetailsForm from 'scoreboard/components/two-column-details-form'

/**
 * A view for selecting multiple items from a single tree.  The design came from
 * Admin | View Org Permissions.  Also used in Datasets for Rollup filters.  Might
 * be extensible for use in the metric comparison report as well.
 *
 * This approach uses a TreeStream mode of buffering the response until we have the entire tree
 * before parsing using native JSON.parse since we need the entire tree before any user actions can
 * take place. See #21775 for history
 */
var MultiTreeSelector = Backbone.View.extend({

	// this.createGardener() should create a gardener to use to display the tree

	// this.oldSelections() should return any previously-chosen selections

	// this.context should return an object which includes the following properties:
	// title, submitLabel, treeLabel, selectedLabel, noneSelectedKey, cascadeHelpKey
	// may also optionally specify treeIcon, treeIconLabel, emptyTreeMessage (required if tree can be empty)

	// once selections are made, this view will fire a 'done' event with the
	// selections in a hash, where the keys are the tree IDs and the values
	// are whether or not to cascade

	tagName: 'form',

	className: 'multi-tree-selector',

	events: {
		// the info and delete buttons next to existing selections
		'click .info': 'showInfoTooltip',
		'click .delete': 'delete',
		'click .node': 'spotlightRow'
	},

	// views that extend this one may choose to define the template body
	_template: function(ctx) {
		return template(ctx)
	},

	/**
	 * @param {String} [options.bannerDescription] If provided this text will be
	 * displayed in a blue banner.
	 */
	initialize: function(options) {
		this.options = options || {}
		this.gardener = this.createGardener()
		// We need the entire tree to download before we can start show/interacting w/ the nodes since the right pane depends on it
		this.gardener.stream.mode = TreeStream.modes.BUFFER

		this.listenTo(this.gardener, 'select', node => {
			this._checkDisabledMask(this.gardener.$('[data-id=' + node.id + ']'))
			this.select()
		})
		// a hash of the IDs already displayed.  issue #9725 initialize in the
		// initialize method!
		// #18674 #18803 Add the items that are already selected so row
		// highlighting will work
		this._ids = _.cloneDeep(_.get(this.options, 'filter.rollupIsAnyFilters', {}))
		this.constructFormView()

		this.formView.on('error cleared-errors', () => {
			if (this.twoColumnsDetailsForm) {
				this.twoColumnsDetailsForm.resize()
			}
		})

		this.setupStream(this.gardener.stream)

		//if the stream changes, setup the new one
		this.gardener.on('stream', stream => this.setupStream(stream))
	},

	constructFormView: function () {
		this.formView = new FormView(this.formViewOptions())
	},

	formViewOptions: function() {
		return {
			el: this.el,
			submit: this.submit.bind(this),
			guard: false,
			allowCleanSubmission: _.get(this.options, 'allowCleanSubmission', false)
		}
	},

	render: function() {
		var context = this.context()
		if (this._insertTemplate) {
			this._insertTemplate(context)
		} else {
			this.$el.empty().append(this._template(context))
		}

		if (this.options.bannerDescription) {
			this.$('.banner-description')
				.append(this.options.bannerDescription)
				.removeClass('is-not-displayed')
		}

		// setup finder and tree
		this.$('.tree-selector-tree .panel-content').append(this.gardener.el)
		this.gardener.render()
		this.$('.tree-container').addClass('tree-container-light')

		this.loadingDots = new LoadingDots({ el: this.$('.loading-dots-container') })
		this.loadingDots.render()
		this.$el.addClass('is-loading')
		// after the tree loads, render existing selections and start the formView
		this.gardener._onceTreeRendered(() => {
			this.$('.loading-dots-container').addClass('is-not-displayed')
			this.loadingDots.remove()
			this.$el.removeClass('is-loading')
			this._renderInitialSelections()
		})

		// Check to see if this view is in the DOM yet before calling setupTwoColumnDetailsForm().
		// This is needed because if this view is being rendered outside of the DOM,
		// elements the twoColumnsDetailsForm will need for its setup won't be available yet.
		// this.setupTwoColumnDetailsForm() will need to be called manually by
		// the view using this multi-tree-selector in that case.
		if (this.$el.closest(document.documentElement).length > 0) {
			this.setupTwoColumnDetailsForm()
		}

		this._listenForTreeRebind()

		this.postRenderGardener(context)

		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		this.cascadeTooltip = new CascadeTooltip({
			attachTo: this.$('.cascade-help'),
			helpKey: this.context().cascadeHelpKey
		})

		// start right away, or else if trees haven't loaded yet we'll get an
		// error if the user tries to click on Done
		this.formView.start()

		return this
	},

	_getScrollContainer: function()  {
		// We need to search the dom tree to find .dialog-body, the scrolling container.
		// It could be a parent of this.el or a child, or this.el itself.
		var scrollContainer
		if (this.$('.dialog-body').length > 0) {
			// .dialog-body is a child of this.el
			scrollContainer = this.$('.dialog-body')
		} else if (this.$el.closest('.dialog-body').length > 0) {
			// .dialog-body is a parent of this.el
			scrollContainer = this.$el.closest('.dialog-body')
		} else if (this.$el.hasClass('dialog-body')) {
			// .dialog-body is this.el
			scrollContainer = this.$el
		}
		return scrollContainer
	},

	_isDialog: function() {
		return true
	},

	setupTwoColumnDetailsForm: function() {
		var scrollContainer = this._getScrollContainer()
		this.twoColumnsDetailsForm = new TwoColumnDetailsForm(_.extend({}, {
			el: this.el,
			gardener: this.gardener,
			isDialog: this._isDialog(),
			getScrollContainer: () => scrollContainer
		}, this._getTwoColumnDetailsFormOptions()))
	},

	// To be overridden in views that extend this one
	_getTwoColumnDetailsFormOptions: function() {
		return {}
	},

	/**
	 * Invoked during the render phase after the gardener is rendered.
	 * Allows subclasses to add additional listeners, etc.
	 */
	postRenderGardener: function(context) {
		// #7367 handle a non-existent tree
		this.gardener.on('error', () => this._onGardenerError(context))
		this.gardener.on('search', () => {
			this._toggleSelectedNodeIds()
			this.setActiveTreeActionsButton.bind(this)
		})
		// When projects change, wait for the tree to render and mark nodes selected if
		// there are any.
		this.gardener.on('projectId', () => {
			this.gardener._onceTreeRendered(() => this._toggleSelectedNodeIds())
			this._listenForTreeRebind()
		})
	},

	// Each time tree nodes rebind, call _toggleSelectedNodeIds in case there
	// are newly inserted/visible tree nodes which should appear as selected.
	// Wait an extra tick for the node to show up in the DOM.
	_listenForTreeRebind: function() {
		this.gardener.tree.on('rebind', () => process.nextTick(() => {
			this._toggleSelectedNodeIds()
		}))
	},

	// Toggles the selected state of all the ids contained within this._ids
	_toggleSelectedNodeIds: function() {
		if (_.isNil(this._ids)) {
			return
		}

		_.each(this._ids, (val, id) => this._toggleTreeNode(id, val))
	},

	_onGardenerError: function(context) {
		this.loadingDots.remove() // Clear loading dots appended to the tree-selector-table
		this.gardener.clearLoadingDots()
		var emptyMessage = `<div class="empty-tree">${_.escape(this.context().emptyTreeMessage)}</div>`
		this.showGardenerErrorMessage(emptyMessage)
	},

	/**
	 * Allows subclasses to provide a different error message
	 * @param emptyMessage The empty tree message from the context
	 */
	showGardenerErrorMessage: function(emptyMessage) {
		this.gardener.$el.addClass('tree-container-light').html(emptyMessage)
	},

	setupStream: function(stream) {
		//setup the tree actions anytime we need to handle a new stream (and therefore tree) in the gardener
		stream.once('end', () => {
			// Once the stream has ended and we have all the tree data, set up the tree actions.
			// Do this immediately instead of on the `end` event of the stream so users can start interacting and selecting nodes
			// See issue #21775
			this.$('.tree-container').addClass('tree-container-light')
			_.result(this.treeActions, 'remove')
			this.treeActions = new TreeActions(_.assign({}, _.get(this.gardener, 'options', {}), {
				el: this.gardener.tree.el.node(),
				tree: this.gardener.tree,
				buttonContent: i18n.get('common.add')
			}, this.options.treeActions)).render()
			this.treeActions.on('selected', this.selectItem.bind(this))
			this.treeActions.on('selectionDisabled', this.spotlightRow.bind(this))
			this.treeActions.on('nodeActivated', node => this._checkDisabledMask(node))
		})
	},

	_checkDisabledMask: function(node) {
		if (node.hasClass('is-disabled') && !node.find('.label-mask').hasClass('is-checked')) {
			node.addClass('disable-check-transition')
			this.treeActions.applyCheckmark(node)
		}
	},

	_renderInitialSelections: function() {
		var selections = _.transform(this.oldSelections(), this.transformSelection.bind(this), [])
		this._renderInitialRows(_.sortBy(selections, this.selectionsSortBy()))
		// must go after rendering the selections
		this.toggleTableMessage()
		// After rendering the selections, update the buttons
		this.formView.valid = !this.formView.test()
		// #31270 only enable the submit button if clean submissions allowed
		this.formView._updateButtons(!!this.formView.options.allowCleanSubmission)
	},

	_renderInitialRows (data) {
		//#22492 when there are a lot of initial selection to render, build them all at once and then append to the
		//DOM for performance
		this.$('.table tbody:not(.loading-dots-tbody)').append(_.map(data, datum => this._buildRow(datum)[0]))
	},

	_buildRow (data) {
		let treeItem = this.gardener.tree.get(data.id)
		let order = _.result(treeItem, 'order', null)

		this._ids[data.id] = true
		let row = this.rowTemplate(_.assign({order}, data))

		this._newRow = row[0]

		// Mark the corresponding tree node as having already been selected if the tree
		// item exsist.
		if (!_.isNil(treeItem)) {
			this._toggleTreeNode(treeItem.id, true)
		}

		// return the row so subclasses can modify it
		return row
	},

	/**
	 * Toggle the 'is-disabled' look for tree nodes based on whether they've been
	 * added to the table or not
	 * @param  {Number} id            id of the tree node to edit
	 * @param  {[type]} notSelectable true if node is NOT selectable, else false
	 */
	_toggleTreeNode (id, notSelectable) {
		this.gardener.$el.find('li[data-id=' + id + ']')
			.toggleClass('is-disabled', notSelectable)
			.removeClass('disable-check-transition', notSelectable)

	},

	/**
	 * @returns A sort value that is compatible with the 2nd operand to _.sortBy
	 */
	selectionsSortBy: function() {
		return ({label}) => (label || '').toLowerCase()
	},

	/**
	 * Creates the objects for the context which is used to render the rows
	 * @see multi-tree-selector-row.hbs
	 *
	 * @param {Array} selectionAccumulator
	 * @param {Boolean} cascade
	 * @param orgId
	 * @private
	 */
	transformSelection: function(selectionAccumulator, cascade, orgId) {
		var treeItem = this.gardener.tree.get(orgId)
		if (treeItem) { // could be null if we have an invalid filter
			selectionAccumulator.push(_.assign(this.initItemData(treeItem), {cascade}))
		}
	},

	resize: function() {
		this.gardener.resize()
	},

	/**
	 * Applies a row template and return a JQuery representation of a <tr>
	 * @param ctx
	 * @return {jQuery}
	 */
	rowTemplate: function(ctx) {
		return $(rowTemplate(ctx))
	},

	/**
	 * @param data The return value of this.initItemData
	 */
	row: function(data) {
		let treeItem = this.gardener.tree.get(data.id)
		let order = _.result(treeItem, 'order', null)

		let row = this._buildRow(data)
		let rows = this.$('.table tbody:not(.loading-dots-tbody) [data-order]')
		if (order == null || rows.length == 0) {
			this.$('.table tbody:not(.loading-dots-tbody)').append(row)
		} else {
			let node = this.getRowInsertionPoint(rows, treeItem, order)
			if (node) {
				$(node).before(row)
			} else {
				this.$('.table tbody:not(.loading-dots-tbody)').append(row)
			}
		}
		return row
	},

	/**
	 * @param rows all existing rows
	 * @param order the order attribute from the newly selected item (leftBound)
	 * @return the node that a new row should be inserted before
	 */
	getRowInsertionPoint: function (rows, treeItem, order) {
		//#19525 list in hierarchical order
		let orderedRows = _.sortBy(rows, (row) => {
			return _.parseInt(row.getAttribute('data-order'), 10)
		})
		let node = _.find(orderedRows, (row) => {
			return _.parseInt(row.getAttribute('data-order'), 10) > order
		})
		return node
	},

	select: function() {
		this.$('.add').prop('disabled', false)
	},

	// subview can override to provide their own data requirements per selection
	initItemData: function(treeItem) {
		return {
			id: treeItem.id,
			label: treeItem.label,
			descendants: treeItem.descendants,
			// #12663 cascade by default
			cascade: _.get(this, 'cascade', true)
		}
	},

	selectItem: function(e) {
		var treeItem = this.gardener.tree.selected()

		// If the nodes get re-drawn after toggling, they return to their active state.
		// Apply the checkmark/disable them before checking if the row should be highlighted.
		this.$('.add').prop('disabled', true)
		this.treeActions.applyCheckmark()

		if (this._ids[treeItem.id]) {
			var row = this.$('.table tr[data-id=' + treeItem.id + ']')
			spotlight(row)
			return
		}

		this.formView.change(e)

		// if the item wasn't already added, add it
		this.row(this.initItemData(treeItem))
		let newRow = this._newRow
		if (this._newRow) {
			this._newRow.scrollIntoView()
			spotlight(this._newRow)
			delete this._newRow
		}

		// #12607 must go after rendering the row
		this.toggleTableMessage()

		this.twoColumnsDetailsForm.resize()

		return newRow
	},

	spotlightRow: function(target) {
		var target = $(target)
		var treeItem = this.gardener.tree.selected()
		// If the spotlightRow method was called from a disabled label mask being clicked,
		// set nodeId based on its parent node. Otherwise, choose the selected node's id.
		var nodeId = target.hasClass('label-mask') ? this.$('.node').has(target).data('id') : treeItem.id
		// if the item was already added, spotlight it
		if (this._ids[nodeId]) {
			var row = this.$('.table tr[data-id=' + nodeId + ']')
			spotlight(row)
			return row
		}
	},

	/**
	 * Returns the new selections the user made on the form, e.g.
	 * {
	 *     '3': true,
	 *     '4': false
	 * }
	 */
	newSelections: function() {
		var selections = {}
		this.$('.table tbody:not(.loading-dots-tbody) tr[data-id]').each(function(i, el) {
			var id = $(el).data('id')
			var cascade = $(el).find('.cascade input')[0].checked
			selections[id] = cascade
		}.bind(this))
		return selections
	},

	submit: function(e) {
		var selections = this.newSelections()
		var treeItems = _.map(selections, function(cascade, id) {
			return this.gardener.tree.get(id)
		}.bind(this))
		this.trigger('done', selections, treeItems, this.formView.dirty)
	},

	showInfoTooltip: function(e) {
		this.infoTooltip && this.infoTooltip.remove()
		var id = $(e.currentTarget).closest('tr').data('id')
		this.infoTooltip = Breadcrumbs.tooltip({
			target: e.currentTarget,
			id: id,
			gardener: this.gardener
		})
	},

	setActiveTreeActionsButton: function() {
		this.treeActions.onSearch()
	},

	'delete': function(e) {
		if ($(e.target).hasClass('is-disabled')) {
			return
		}

		this.formView.change(e)
		var row = $(e.currentTarget).closest('tr')
		var id = row.data('id')
		this._toggleTreeNode(id, false)
		delete this._ids[id]
		if (this.treeActions) {
			this.treeActions.removeCheckmark(row.data('id'))
		}
		row.remove()
		this.toggleTableMessage()
		this.twoColumnsDetailsForm.resize()
	},

	// toggle the "There are no ____ selected" message that can appear
	// on the right side of the dialog
	toggleTableMessage: function() {
		var noneSelected = this.$('tbody:not(.loading-dots-tbody) [data-id]').length === 0
		this.$('.none-selected').toggleClass('is-not-displayed', !noneSelected)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.gardener, this.cascadeTooltip, this.infoTooltip,
			this.covertButtonTooltips, this.loadingDots, this.treeActions,
			this.twoColumnsDetailsForm], 'remove')
	}

})

export default MultiTreeSelector

MultiTreeSelector.__filename__ = 'components/multi-tree-selector.js'
